import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../Components/Footer';
import { getPricingRegion, getRegionalPricing, INDIA_PRO_AVAILABLE } from '../utils/plans';
import './HomePage.css';

const inviteCopies = [
  {
    text: "Create and manage your soap recipes, batches, and inventory — all in one place.",
    cta: "Sign up today"
  },
  {
    text: "All the tools you need for soapmaking, in one simple platform.",
    cta: "Create your free account"
  },
  {
    text: "Build recipes, track batches, and manage ingredients with ease.",
    cta: "Join now"
  },
  {
    text: "Organize your soapmaking from start to finish.",
    cta: "Get started"
  },
  {
    text: "Plan, calculate, and manage your soap production in one place.",
    cta: "Create an account"
  },
  {
    text: "Manage recipes, production, and inventory without the hassle.",
    cta: "Sign up now"
  },
  {
    text: "Everything you need to run your soapmaking process efficiently.",
    cta: "Start today"
  },
  {
    text: "Create recipes. Track batches. Manage stock.",
    cta: "Join now"
  },
  {
    text: "A simple way to manage your soap business.",
    cta: "Get started for free"
  },
  {
    text: "Keep your soap recipes and production organized and under control.",
    cta: "Create your account today"
  }
];

function InviteStripe() {
  const [index, setIndex] = useState(0);
  const intervalRef = useRef(null);

  const advance = () => {
    setIndex((prevIndex) =>
      prevIndex === inviteCopies.length - 1 ? 0 : prevIndex + 1
    );
  };

  const startInterval = () => {
    intervalRef.current = setInterval(advance, 15000);
  };

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    startInterval();
    return () => stopInterval();
  }, []);

  return (
    <div
      className="borderleft slstripe"
      onMouseEnter={stopInterval}
      onMouseLeave={startInterval}
    >
      <span key={index} className="slstripe-content stripelink">
        {inviteCopies[index].text}{' '}
        <Link to="/signup">{inviteCopies[index].cta} ❯</Link>

      </span>
    </div>
  );
}

export default function HomePage() {
  const [heroMedia, setHeroMedia] = useState(null);
  const monthlyPrice = getRegionalPricing().monthly;

  // Select random hero image or video from local directory
  useEffect(() => {
    const heroMediaOptions = [
      { type: 'image', src: '/Images/heroimages/pexels-karola-g-4888825.jpg' },
      { type: 'image', src: '/Images/heroimages/pexels-nietjuhart-18210710.jpg' },
      { type: 'image', src: '/Images/heroimages/pexels-taffy-buoniconti-1080066-6815656.jpg' },
      { type: 'video', src: '/Images/heroimages/6693842-uhd_4096_2160_30fps.mp4' }
    ];
    // The hero video is about 38 MB; never send it to phones or data-saver connections.
    const lightweight = window.matchMedia?.('(max-width: 900px)').matches || navigator.connection?.saveData;
    if (lightweight) heroMediaOptions.pop();
    const randomIndex = Math.floor(Math.random() * heroMediaOptions.length);
    setHeroMedia(heroMediaOptions[randomIndex]);
  }, []);

  const tools = [
    {
      to: '/calculator',
      title: 'Lye calculator',
      description: 'NaOH, KOH and dual-lye calculations with superfat and water settings, plus live soap properties and fatty acid profiles.'
    },
    {
      to: '/melt-and-pour',
      title: 'Melt & pour calculator',
      description: 'Working from a premade base? Weigh out base, fragrance and colour for any number of bars and see the cost of each one.'
    },
    {
      to: '/inventory',
      title: 'Inventory',
      description: 'Track your oils, additives and supplies, with low-stock alerts and the cost of everything you use.'
    },
    {
      to: '/batches',
      title: 'Batch tracking',
      description: 'Log every batch, follow it through the cure and know when it is ready to sell.'
    },
    {
      to: '/generator',
      title: 'Recipe generator',
      description: 'Starting-point oil blends for the bar you want, ready to check in the lye calculator.'
    },
    {
      to: '/labels',
      title: 'Labels',
      description: 'Product labels with ingredients, batch details and your own branding, ready to print.'
    },
  ];

  const workflow = [
    {
      title: 'Plan the recipe',
      description: 'Use the free lye calculator for cold and hot process, or the melt & pour calculator for premade bases. Every recipe shows its cost per bar.'
    },
    {
      title: 'Make and track batches',
      description: 'Log each batch, deduct ingredients from your stock, and see on the curing calendar when it is ready to sell.'
    },
    {
      title: 'Sell and see real profit',
      description: 'Record sales against batches, print labels, and see which products actually make you money.'
    }
  ];

  return (
    <div className="homepage">
      {/* Hero Section - Random Hero Image with Logo Overlay */}
      <section className="blog-hero-section borderleft">
        <div 
          className="blog-hero-image"
          style={{ 
            backgroundImage: heroMedia && heroMedia.type === 'image'
              ? `url(${heroMedia.src})` 
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}
        >
          {heroMedia && heroMedia.type === 'video' && (
            <video
              className="blog-hero-video"
              src={heroMedia.src}
              autoPlay
              muted
              loop
              playsInline
            />
          )}
          <div className="blog-hero-overlay">
            <img 
              src="/Images/sllogowhite.png" 
              alt="SoapLab" 
              className="blog-hero-logo"
            />
          </div>
        </div>
      </section>

      <InviteStripe />

      <div className="borderleft">
        <section className="sl-band hp-intro">
          <div>
            <span className="sl-eyebrow">For small soap businesses</span>
            <h1 className="sl-title">Everything your soap business needs, in one place.</h1>
            <p className="sl-lede">
              Lye and melt &amp; pour calculators, recipes, stock, batches and sales — whether you make
              soap from scratch or from a premade base.
            </p>
            <div className="sl-actions">
              <Link to="/signup" className="sl-btn sl-btn-primary">Create a free account</Link>
              <Link to="/pricing" className="sl-arrow-link">See plans</Link>
            </div>
            <p className="hp-note">Free forever for the basics. No card needed.</p>
          </div>

          <nav className="hp-try" aria-label="Free tools">
            <span className="sl-eyebrow">Try now, no account needed</span>
            <Link to="/calculator" className="hp-try-link">
              <strong>Lye calculator</strong>
              <span>Cold and hot process, NaOH and KOH</span>
            </Link>
            <Link to="/melt-and-pour" className="hp-try-link">
              <strong>Melt &amp; pour calculator</strong>
              <span>Base, fragrance and colour for any number of bars</span>
            </Link>
            <Link to="/templates" className="hp-try-link">
              <strong>Recipe templates</strong>
              <span>Tested starting recipes to adapt</span>
            </Link>
            <Link to="/blog" className="hp-try-link">
              <strong>Guides</strong>
              <span>Safety, troubleshooting and selling</span>
            </Link>
          </nav>
        </section>

        <section className="sl-band">
          <div className="hp-band-head">
            <span className="sl-eyebrow">Tools</span>
            <h2 className="sl-subtitle">Made for the way small soap businesses work</h2>
          </div>
          <div className="sl-rule-grid">
            {tools.map((tool, index) => (
              <Link key={tool.title} to={tool.to} className="sl-rule-cell">
                <span className="sl-number">{String(index + 1).padStart(2, '0')}</span>
                <h3>{tool.title}</h3>
                <p>{tool.description}</p>
              </Link>
            ))}
          </div>
        </section>

        <section className="sl-band">
          <div className="hp-band-head">
            <span className="sl-eyebrow">How it works</span>
            <h2 className="sl-subtitle">From recipe to profit</h2>
            <p className="sl-lede">One connected workflow instead of spreadsheets and notebooks.</p>
          </div>
          <ol className="sl-rule-grid hp-steps">
            {workflow.map((step, index) => (
              <li key={step.title}>
                <span className="sl-number">Step {index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </li>
            ))}
          </ol>
        </section>

        <div className="slstripe stripelink hp-stripe">
          The lye calculator is free to use, with no sign-up needed.{' '}
          <Link to="/calculator">Open the calculator ❯</Link>
        </div>

        <section className="sl-band">
          <div className="hp-band-head">
            <span className="sl-eyebrow">Pricing</span>
            <h2 className="sl-subtitle">Start free. Upgrade when you need more.</h2>
          </div>
          <div className="sl-rule-grid cols-2 hp-plans">
            <div>
              <h3>Free</h3>
              <span className="sl-figure">{monthlyPrice.currency}0</span>
              <span className="sl-figure-label">forever</span>
              <ul className="hp-plan-list">
                <li>Full lye calculator</li>
                <li>Save up to 10 recipes</li>
                <li>30 ingredients and 5 batch records</li>
                <li>Basic soap properties</li>
                <li>Recipe templates</li>
              </ul>
              <Link to="/signup" className="sl-btn sl-btn-secondary">Start free</Link>
            </div>
            <div className="hp-plan-pro">
              <h3>Pro <span className="hp-plan-tag">{getPricingRegion() === 'india' && !INDIA_PRO_AVAILABLE ? 'Opening soon in India' : 'Most popular'}</span></h3>
              <span className="sl-figure">{monthlyPrice.currency}{monthlyPrice.price}</span>
              <span className="sl-figure-label">per month</span>
              <ul className="hp-plan-list">
                <li>Everything in Free</li>
                <li>Unlimited recipes, ingredients and batches</li>
                <li>Label generator</li>
                <li>Sales and profit reports</li>
                <li>Priority support</li>
              </ul>
              <Link to="/pricing" className="sl-btn sl-btn-primary">See Pro plans</Link>
            </div>
          </div>
        </section>

        <section className="sl-band hp-final">
          <h2 className="sl-title">Ready to make better soap?</h2>
          <p className="sl-lede">
            Start with the free calculators, then bring your recipes, stock and batches together when you are ready.
          </p>
          <div className="sl-actions">
            <Link to="/signup" className="sl-btn sl-btn-primary">Create a free account</Link>
            <Link to="/calculator" className="sl-arrow-link">Try the calculator</Link>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
